import { useCallback, useState } from 'react';
import type { Designation } from '@altyor-kronos/shared-types';
import { apiList } from '../../core/data/restClient';
import { onMutation } from '../../core/data/mutationSignal';

/**
 * On-demand designation loader keyed by `projectId`, with a module-level
 * in-memory cache.
 *
 * Each editable recap row needs the designation options for *its own*
 * project (rows may span different projects). Subscribing to every
 * project up front would be wasteful, so loading is lazy: `load()` is
 * called when a row's project/designation Autocomplete first opens. The
 * result is cached, so repeated opens — and other rows on the same
 * project — are instant and cost no extra reads.
 *
 * A one-shot fetch is used rather than a live feed: the recap only needs
 * the option list at edit time. Designations change rarely; a
 * stale-by-seconds list is fine for a picker.
 */
const cache = new Map<string, Designation[]>();
const inflight = new Map<string, Promise<Designation[]>>();

// Without this the cache never expires: create or rename a designation on a
// project, open the Tracker picker for that project, and the change was missing
// for the rest of the session (the entry was already cached). Dropping the whole
// map on any write is free — it refills lazily on the next picker open.
// ponytail: clears every project's entry, not just the one written to. The map
// holds a handful of small lists; targeting the exact project would mean
// threading the collection path through the signal for no measurable gain.
onMutation(() => {
  cache.clear();
  inflight.clear();
});

/** Fetch (or return cached) designations for a project. Cache-aware. */
export function fetchDesignationsFor(projectId: string): Promise<Designation[]> {
  const cached = cache.get(projectId);
  if (cached) return Promise.resolve(cached);
  const pending = inflight.get(projectId);
  if (pending) return pending;

  const fetch: Promise<Designation[]> = apiList<Designation & { id: string }>(
    `/projects/${projectId}/designations`,
  );
  const p = fetch
    .then((designations) => {
      cache.set(projectId, designations);
      inflight.delete(projectId);
      return designations;
    })
    .catch((err) => {
      inflight.delete(projectId);
      throw err;
    });
  inflight.set(projectId, p);
  return p;
}

/** Test-only: reset the module cache between cases. */
export function __clearDesignationCache(): void {
  cache.clear();
  inflight.clear();
}

export interface DesignationsForState {
  designations: Designation[];
  loading: boolean;
  /** Trigger a (cache-aware) load. Safe to call repeatedly. */
  load: () => void;
}

export function useDesignationsFor(projectId: string): DesignationsForState {
  const [designations, setDesignations] = useState<Designation[]>(
    () => cache.get(projectId) ?? [],
  );
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    if (!projectId) return;
    const cached = cache.get(projectId);
    if (cached) {
      setDesignations(cached);
      return;
    }
    setLoading(true);
    fetchDesignationsFor(projectId)
      .then((ds) => {
        setDesignations(ds);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [projectId]);

  return { designations, loading, load };
}
